import React from 'react'
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Paper,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { Playlist, Sequence } from '../types/catalog'

interface CatalogProps {
  playlists: Playlist[]
  onSequenceSelect: (sequence: Sequence) => void
  selectedSequence: Sequence | null
}

const Catalog: React.FC<CatalogProps> = ({ playlists, onSequenceSelect, selectedSequence }) => {
  return (
    <Box>
      {playlists.map((playlist, playlistIndex) => (
        <Box
          key={playlistIndex}
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              mb: 2,
            }}
          >
            <Box
              component="img"
              src={playlist.thumbnail.url}
              alt={playlist.title}
              sx={{
                width: 192,
                height: 128,
                aspectRatio: '12/8',
                borderRadius: 2,
                objectFit: 'cover',
                mr: { xs: 0, sm: 3 },
                mb: { xs: 2, sm: 0 },
              }}
            />
            <Typography variant="h4" component="h2">
              {playlist.title}
            </Typography>
          </Box>

          <Paper elevation={0} sx={{ bgcolor: 'transparent' }}>
            <List sx={{ width: '100%', bgcolor: 'transparent' }}>
              {playlist.sequences.map((sequence, sequenceIndex) => {
                const isSelected = selectedSequence?.title === sequence.title

                return (
                  <ListItem
                    key={sequenceIndex}
                    disablePadding
                    sx={{
                      borderBottom: sequenceIndex < playlist.sequences.length - 1 ? 1 : 0,
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => onSequenceSelect(sequence)}
                      sx={{
                        py: 2,
                        overflow: 'hidden',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          '& .stream-indicator': {
                            transform: 'translateX(0)',
                            opacity: 1,
                          },
                          '& .content-wrapper': {
                            transform: 'translateX(0)',
                          },
                        },
                        '&.Mui-selected': {
                          bgcolor: 'action.selected',
                          '&:hover': {
                            bgcolor: 'action.selected',
                            '& .stream-indicator': {
                              transform: 'translateX(0)',
                              opacity: 1,
                            },
                            '& .content-wrapper': {
                              transform: 'translateX(0)',
                            },
                          },
                        },
                      }}
                    >
                      {/* STREAM button that slides in from left */}
                      <Box
                        className="stream-indicator"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          transform: 'translateX(-100%)',
                          opacity: 0,
                          transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        <PlayArrowIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 'bold',
                            color: 'primary.main',
                            textTransform: 'uppercase',
                          }}
                        >
                          Stream
                        </Typography>
                      </Box>

                      {/* Content that gets pushed to the right */}
                      <Box
                        className="content-wrapper"
                        sx={{
                          transform: 'translateX(-120px)',
                          transition: 'transform 0.3s ease-in-out',
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="h6" component="div">
                              {sequence.title}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {sequence.author}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {/* {sequence.maxTrisPerFrame / 1000}k tris • {sequence.textureResolution}p • {sequence.scenes[0]?.mobile4DS.split('/').pop()} */}
                                {sequence.maxTrisPerFrame / 1000}k tris • {sequence.textureResolution}p
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>

                      {isSelected && (
                        <PlayArrowIcon sx={{ fontSize: 40, color: 'primary.main', flexShrink: 0 }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          </Paper>
        </Box>
      ))}
    </Box>
  )
}

export default Catalog
